import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { useFormData } from '../../hooks';
import { Attachment } from '../../models';
import {
  MESSAGE_ORDER_TYPE_FIRST,
  MESSAGE_ORDER_TYPE_LAST,
  MESSAGE_ORDER_TYPE_MIDDLE,
  MESSAGE_TYPE_RECEIVED,
  MESSAGE_TYPE_SENT
} from '../../utilities';
import ButtonBase from '../ButtonBase';
import Feedback from '../Feedback';
import TagBlock from '../TagBlock';
import DateTime from './DateTime';
// import MessageActions from '../MessageActions';
// import Share from '../Share';
import { MessageBase, MessageBaseContent } from './MessageBase';

const Message = ({
  children,
  type,
  orderType,
  textFontFamily,
  textFontSize,
  textColor,
  backgroundColor,
  borderRadius,
  buttonColor,
  buttonBackgroundColor,
  disablePadding,
  feedback,
  actionsHorizontalPosition,
  contentProps,
  attachments,
  onLike,
  onDislike,
  isDivider,
  isCarousel,
  MesIndex,
  crossProjectName,
  answerWithDateTime,
  ...props
}) => {
  const container = useRef(null);
  const [isLiked, setLiked] = useState(false);
  const [isDisliked, setDisliked] = useState(false);

  useEffect(() => {
    const rootEl = container;
    const iframeEl = rootEl.current.querySelector('iframe');
    if (iframeEl) {
      const parent = iframeEl.parentNode;
      if (parent.children.length === 1) {
        parent.classList.add('iframe-wrap');
      } else {
        parent.classList.add('container-with-iframe');
      }
    }
  }, []);

  useFormData(container);

  const handleLike = () => {
    if (onLike) {
      setLiked(true);
      onLike();
    }
  };

  const handleDislike = () => {
    if (onDislike) {
      setDisliked(true);
      onDislike();
    }
  };

  let renderLast = false;
  const MesIndexCount = Number(localStorage.getItem('MesIndexCount'));

  if (feedback) {
    if (MesIndex > MesIndexCount) {
      localStorage.setItem('MesIndexCount', MesIndex);
    }
    if (MesIndex === Number(localStorage.getItem('MesIndexCount'))) {
      renderLast = true;
    }
  }

  return (
    <MessageBase isDivider={isDivider} {...props}>
      <Content
        ref={container}
        type={type}
        orderType={orderType}
        textFontFamily={textFontFamily}
        textFontSize={textFontSize}
        borderRadius={borderRadius}
        textColor={textColor}
        backgroundColor={backgroundColor}
        buttonColor={buttonColor}
        buttonBackgroundColor={buttonBackgroundColor}
        disablePadding={disablePadding}
        className={type === MESSAGE_TYPE_SENT ? 'speech-bubble' : 'speech-bubble1'}
        isCarousel={isCarousel}
        {...contentProps}
      >
        {crossProjectName && <Tag mesType={type}>{crossProjectName}</Tag>}
        {children}
        {answerWithDateTime && <DateTime />}

        {renderLast && (
          <Feedback
            onLike={handleLike}
            onDislike={handleDislike}
            feedback={feedback}
            horizontalPosition={actionsHorizontalPosition}
            isLiked={isLiked}
            isDisliked={isDisliked}
          />
        )}
      </Content>
    </MessageBase>
  );
};

Message.propTypes = {
  /**
   * Message contents.
   */
  children: PropTypes.node,
  /**
   * Type of message. For example: `sent` or `received`.
   * This property is used to apply specific styles.
   */
  type: PropTypes.oneOf([MESSAGE_TYPE_SENT, MESSAGE_TYPE_RECEIVED]).isRequired,
  /**
   * This property allows you to identify the
   * `first`, `middle` and `last` message and,
   * depending on this, round the corners or apply certain styles.
   */
  orderType: PropTypes.oneOf([
    MESSAGE_ORDER_TYPE_FIRST,
    MESSAGE_ORDER_TYPE_MIDDLE,
    MESSAGE_ORDER_TYPE_LAST
  ]).isRequired,
  /**
   * Similar to the css `font-family` property,
   * used to determine the font-family of the message text.
   */
  textFontFamily: PropTypes.string,
  /**
   * Similar to the css `font-size` property,
   * used to determine the font-size of the message text.
   */
  textFontSize: PropTypes.number,
  /**
   * Similar to the css `color` property,
   * used to determine the color of the message text.
   */
  textColor: PropTypes.string,
  /**
   * Similar to the css `background-color` property,
   * used to determine the color of the message background.
   */
  backgroundColor: PropTypes.string,
  /**
   * Radius of fillet corners.
   */
  borderRadius: PropTypes.number,
  /**
   * Similar to the css `color` property,
   * used to determine the color of the form button.
   */
  buttonColor: PropTypes.string,
  /**
   * Used to determine the background-color and border-color of the form button.
   */
  buttonBackgroundColor: PropTypes.string,
  /**
   * Disables indentation in the message.
   */
  disablePadding: PropTypes.bool,
  /**
   * If `true` (Default: `false`) a component will be shown which
   * is intended for user feedback.
   */
  feedback: PropTypes.bool,
  /**
   * If `true` (Default: `false`) a component will be shown horizontal which
   * is intended for user feedback and related question.
   */
  actionsHorizontalPosition: PropTypes.bool,
  /**
   * Prop applied to the `Content` component.
   */
  contentProps: PropTypes.shape({}),
  /**
   * Message attachment array.
   * May contain related questions, photos, videos, etc.
   */
  attachments: PropTypes.arrayOf(PropTypes.instanceOf(Attachment)),
  /**
   * Callback fired when user clicks on `Like` button.
   */
  onLike: PropTypes.func,
  /**
   * Callback fired when user clicks on `Dislike` button.
   */
  onDislike: PropTypes.func,
  /**
   * Show divider line over message or no
   */
  isDivider: PropTypes.bool,
  /**
   * Show carousel message or no
   */
  isCarousel: PropTypes.bool,
  /**
   * Message index for detect the last message and show feedback
   */
  MesIndex: PropTypes.number,
  /**
   * Cross project name
   */
  crossProjectName: PropTypes.string,
  /**
   * Answer must contain date & time
   */
  answerWithDateTime: PropTypes.bool
};

Message.defaultProps = {
  feedback: false,
  actionsHorizontalPosition: false,
  contentProps: {},
  textFontFamily: '"Lato", sans-serif',
  textColor: '#333',
  backgroundColor: '#fff',
  borderRadius: 0,
  attachments: [],
  isDivider: false,
  isCarousel: false,
  crossProjectName: '',
  answerWithDateTime: false
};

const Tag = styled(TagBlock)`
  ${({ mesType }) => {
    if (mesType === MESSAGE_TYPE_SENT) {
      return css`
        color: #e7e7e7;
      `;
    }

    return null;
  }}
`;

export const Content = styled(MessageBaseContent)`
  & > ${ButtonBase} {
    margin-top: 8px;
    margin-right: 8px;
  }

  & > svg {
    vertical-align: middle;
  }
`;

export default Message;
